const asText = (source: Record<string, unknown>, keys: string[]): string => {
  const value = keys.map((key) => source?.[key]).find(Boolean);
  return String(value || '').trim();
};

const LENS_ACRONYM_IGNORED_WORDS = new Set(['and', 'of', 'the']);

const ENTITY_LENS_ABBREVIATIONS: Record<string, string> = {
  'Beneficial Ownership': 'BE',
  'AD CVD Entities': 'AD/CVD',
  'Consumer Protection': 'CP',
  'EU Restricted Parties': 'EU',
  'ESG Entities': 'ESG',
  'Fines Penalties and Enforcement Actions': 'FPEA',
  'Forced Labor Entities': 'FLE',
  'High Risk Trade': 'HRT',
  'Most Wanted': 'MW',
  'Trade Alerts': 'TA',
  'US Exclusion Lists': 'US-EX',
  'US Restricted Parties': 'US',
  'Worldwide Restricted Parties': 'WW',
  Carriers: 'CR',
  'Restricted Ownership and Affiliates': 'RE',
  'AD CVD Commodities': 'AD/CVD',
  'ESG Commodities': 'ESG',
  'Forced Labor Commodities': 'FL',
  'Nomenclatures - HTS Codes Schedule B and CN Codes': 'HS',
  'Nomenclatures – HTS Codes Schedule B and CN Codes': 'HS',
  'PGA Codes': 'PGA',
  'Partner Government Agency Filings': 'PGA',
  'Sensitive Technology': 'ST',
  'US Duties & Tariffs': 'TR',
  'US Monitoring and Regulations': 'USMR',
};

export const parseLensNames = (raw: unknown): string[] => {
  if (raw === null || raw === undefined) {
    return [];
  }
  if (Array.isArray(raw)) {
    return Array.from(
      new Set(raw.map((value) => String(value).trim()).filter(Boolean)),
    );
  }
  return Array.from(
    new Set(
      String(raw)
        .split(/[;|,]/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
};

export const parseEntityLensNames = (
  source: Record<string, unknown> | null | undefined,
): string[] => parseLensNames(source?.['BITE Lens'] ?? source?.Lens);

export const formatEntityLensCode = (lens: string): string => {
  const normalized = lens.trim();
  if (!normalized) {
    return '--';
  }
  const configuredAbbreviation = ENTITY_LENS_ABBREVIATIONS[normalized];
  if (configuredAbbreviation) {
    return configuredAbbreviation;
  }

  const abbreviation = normalized
    .split(/[\s–—-]+/)
    .filter(
      (word) =>
        word.length > 0 && !LENS_ACRONYM_IGNORED_WORDS.has(word.toLowerCase()),
    )
    .map((word) => (/^[A-Z0-9/]+$/.test(word) ? word : word[0]))
    .join('')
    .toUpperCase();

  return abbreviation || normalized.slice(0, 3).toUpperCase();
};

export const getEntityLensName = (
  source: Record<string, unknown> | null | undefined,
): string => asText(source || {}, ['BITE Lens', 'Lens']);

export const getEntityLensCode = (
  source: Record<string, unknown> | null | undefined,
): string => formatEntityLensCode(parseEntityLensNames(source)[0] || '');

export const getEntityLensBadgeClassName = (code: string): string => {
  const modifier = code
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return modifier ? `entity-lens-badge ${modifier}` : 'entity-lens-badge';
};
